"use client";

import React, { useMemo } from "react";
import { User } from "lucide-react";
import { ContactPopover } from "@/features/contacts/components/ContactPopover";
import { useContact } from "@/features/contacts/hooks";
import { Client } from "@/lib/types/database";
import type {
  SmartContactDisplayProps,
  SmartContactChipProps,
} from "@/components/shared/UniversalCRMTable/types/shared";

export function SmartContactDisplay({
  contactId,
  contactData,
  fallbackText = "Unknown",
  showIcon = true,
  className = "",
  disabled = false,
  showPopover = true,
}: SmartContactDisplayProps & { disabled?: boolean }) {
  // Only fetch contact if we don't have contactData and need to lookup
  const { data: contact, isLoading } = useContact(contactId, { enabled: !contactData });

  // Memoize contact lookup to prevent unnecessary re-renders
  const contactInfo = useMemo(() => {
    return contactData || contact;
  }, [contactData, contact]);

  // Memoize display name calculation
  const displayName = useMemo(() => {
    if (!contactInfo) return fallbackText;
    return contactInfo.name || contactInfo.email || "Unknown Contact";
  }, [contactInfo, fallbackText]);

  // Show loading state if we're fetching and don't have data
  if (!contactInfo && isLoading && !contactData) {
    return <span className={`text-gray-400 ${className}`}>Loading...</span>;
  }

  if (!contactInfo) {
    return <span className={`text-gray-400 ${className}`}>{fallbackText}</span>;
  }

  // If disabled, just show the text without popover
  if (disabled) {
    return (
      <div className={`flex items-center gap-2 rounded px-1 ${className}`}>
        {showIcon && <User className="h-4 w-4 text-gray-400" />}
        <span className="text-sm text-card-foreground">{displayName}</span>
      </div>
    );
  }

  // Expand inline (no popover) - e.g. in table cells
  if (!showPopover) {
    return (
      <div className={`flex items-center gap-2 rounded px-1 ${className}`}>
        {showIcon && <User className="h-4 w-4 text-gray-400" />}
        <span className="text-sm text-card-foreground">{displayName}</span>
      </div>
    );
  }

  return (
    <ContactPopover name={contactInfo.name || ""} email={contactInfo.email || ""}>
      <div
        className={`flex items-center gap-2 cursor-pointer hover:bg-card-50 rounded px-1 ${className}`}
      >
        {showIcon && <User className="h-4 w-4 text-gray-400" />}
        <span className="text-sm text-card-foreground hover:text-blue-600">{displayName}</span>
      </div>
    </ContactPopover>
  );
}

export function SmartContactChip({
  contactId,
  contactData,
  fallbackText = "Unknown",
  size = "sm",
  showPopover = true,
}: SmartContactChipProps) {
  const { data: contact, isLoading } = useContact(contactId, { enabled: !contactData });

  // Memoize contact lookup to prevent unnecessary re-renders
  const contactInfo = useMemo(() => {
    return contactData || contact;
  }, [contactData, contact]);

  // Memoize display name and initials calculation
  const { displayName, initials } = useMemo(() => {
    if (!contactInfo) return { displayName: fallbackText, initials: fallbackText.slice(0, 2) };

    const name = contactInfo.name || contactInfo.email || "Unknown Contact";
    const contactInitials = name
      .split(" ")
      .map((n: string) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

    return { displayName: name, initials: contactInitials };
  }, [contactInfo, fallbackText]);

  // Show loading state if we're fetching and don't have data
  if (!contactInfo && isLoading && !contactData) {
    return (
      <span
        className={
          "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-card-100 text-gray-600 dark:text-gray-400"
        }
      >
        Loading...
      </span>
    );
  }

  if (!contactInfo) {
    return (
      <span
        className={
          "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-card-100 text-gray-600 dark:text-gray-400"
        }
      >
        {fallbackText}
      </span>
    );
  }

  if (!showPopover) {
    return (
      <span
        className={
          "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200"
        }
      >
        {size === "sm" ? initials : displayName}
      </span>
    );
  }

  return (
    <ContactPopover name={contactInfo.name || ""} email={contactInfo.email || ""}>
      <span
        className={
          "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 cursor-pointer hover:bg-green-200 dark:bg-green-900/40 dark:text-green-200 dark:hover:bg-green-900/50"
        }
      >
        {size === "sm" ? initials : displayName}
      </span>
    </ContactPopover>
  );
}
